import i18next from "i18next";

import { BillingLocationsTableType, IBillingLocation } from "../types/billingLocations";
import { ICollectionYardsEventApi, ICollectionYardsVisit } from "../types/collectionYards";
import { CurrencySymbol } from "../types/currency";
import { IServerEnumObject } from "../types/enums";
import { IInventorySheetContainer } from "../types/inventorySheet";
import { ICollectionPointLocation } from "../types/location";
import { IReportItem } from "../types/wagons";
import { getDateTimeStrings } from "./dateHandler";
import { EnumName } from "./enums";

export const formatLocationDataForXlsx = (xlsxData: ICollectionPointLocation[]) => {
  return xlsxData.map((data) => {
    return {
      [i18next.t("name")]: data.name,
      [i18next.t("address")]: data.address,
      [i18next.t("containerCount")]: Number(data.containerCount),
      [`${i18next.t("totalTKO")} (${i18next.t("kg")})`]: Number(data.totalTKO),
      [i18next.t("householdMembersCount")]: Number(data.householdMembersCount),
      [`${i18next.t("wastePerHouseholdMember")} (${i18next.t("kg")})`]: Number(data.wastePerHouseholdMember),
      [i18next.t("degreeOfSeparation")]: data.degreeOfSeparation + "%",
      [i18next.t("totalDumpings")]: Number(data.totalDumpings),
      [i18next.t("isPaid")]: Number(data.isPaid) === 1 ? i18next.t("yes") : i18next.t("no"),
      [i18next.t("wcpid")]: data.wcpid,
    };
  });
};

export const formatInventorySheetDataForXlsx = (xlsxData: IInventorySheetContainer[], enums: IServerEnumObject[]) => {
  return xlsxData.map((data) => {
    const wasteTypeResourceCode =
      enums.find((item) => item.name === EnumName.WasteTypes)?.values.find((item) => item.value === data.wasteType)
        ?.resourceCode ?? "";
    const containerTypeResourceCode =
      enums
        .find((item) => item.name === EnumName.ContainerTypes)
        ?.values.find((item) => item.value === data.containerType)?.resourceCode ?? "";

    return {
      [i18next.t("inventoryList.collectionPointName")]: data.collectionPointName,
      [i18next.t("inventoryList.street")]: data.street,
      [i18next.t("inventoryList.streetNumber")]: data.streetNumber,
      [i18next.t("inventoryList.wasteType")]: i18next.t(wasteTypeResourceCode),
      [i18next.t("inventoryList.containerType")]: i18next.t(containerTypeResourceCode),
      [i18next.t("inventoryList.rfid")]: data.rfid,
      [i18next.t("inventoryList.barcode")]: data.barcode,
      [i18next.t("inventoryList.pointId")]: data.pointId,
      [i18next.t("inventoryList.householdMembersCount")]: data.householdMembersCount,
    };
  });
};

export const formatCollectionYardsVisitsForXlsx = (xlsxData: ICollectionYardsVisit[]) => {
  return xlsxData.map((data) => {
    const createdAt = getDateTimeStrings(new Date(data.created));

    return {
      [i18next.t("collectionYardsVisits.created")]: `${createdAt.date} ${createdAt.time}`,
      [i18next.t("collectionYardsVisits.locationName")]: data.locationName,
      [i18next.t("collectionYardsVisits.wcpid")]: data.wcpid?.toString() ?? "",
      [i18next.t("collectionYardsVisits.collectionYardName")]: data.collectionYardName,
      [i18next.t("collectionYardsVisits.wasteTypes")]: data.wasteTypes
        .map((item) => i18next.t(`enums.allWasteTypes.${item}.short`))
        .join(", "),
      [i18next.t("collectionYardsVisits.wasteSum")]: data.wasteSum + " kg",
      [i18next.t("collectionYardsVisits.priceSum")]: data.priceSum + CurrencySymbol.Eur,
      [i18next.t("collectionYardsVisits.paid")]: data.paidAt
        ? i18next.t("enums.answers.yes")
        : i18next.t("enums.answers.no"),
    };
  });
};

export const formatCollectionYardsEventsForXlsx = (xlsxData: ICollectionYardsEventApi[]) => {
  return xlsxData
    .map((data) => {
      const createdAt = getDateTimeStrings(new Date(data.CollectionYardVisits.created));

      return {
        [i18next.t("collectionYardsVisits.created")]: `${createdAt.date} ${createdAt.time}`,
        [i18next.t("collectionYardsVisits.locationName")]: data.CollectionYardVisits.Locations.Name,
        [i18next.t("collectionYardsVisits.wcpid")]: data.CollectionYardVisits.Locations.OutNO?.toString() ?? "",
        [i18next.t("collectionYardsVisits.collectionYardName")]: data.CollectionYardVisits.CollectionYards.name,
        [i18next.t("collectionYardsVisits.wasteType")]: i18next.t(`enums.allWasteTypes.${data.waste_type_id}.short`),
        [i18next.t("collectionYardsVisits.weight")]: data.brutto - data.tara + " kg",
      };
    })
    .reverse();
};

export const formatBillingLocationsForXlsx = (xlsxData: IBillingLocation[], tableType: BillingLocationsTableType) => {
  return xlsxData.map((data) => {
    const enableTime = data.enableTime ? getDateTimeStrings(new Date(data.enableTime)) : null;

    if (tableType === BillingLocationsTableType.Calculation) {
      return {
        [i18next.t("billingLocationsExport.isActive")]: data.isActive
          ? i18next.t("enums.answers.yes")
          : i18next.t("enums.answers.no"),
        [i18next.t("billingLocationsExport.name")]: data.name,
        [i18next.t("billingLocationsExport.householdMembersCount")]: Number(data.householdMembersCount),
        [i18next.t("billingLocationsExport.enableTime")]: enableTime ? enableTime.date : "",
        [i18next.t("billingLocationsExport.previousWaste")]: Number(data.previousWaste),
        [i18next.t("billingLocationsExport.amount")]: Number(data.amount),
        [i18next.t("billingLocationsExport.paymentType")]: i18next.t(`billingPaymentScheduleType.${data.paymentType}`),
      };
    } else if (tableType === BillingLocationsTableType.Billing) {
      return {
        [i18next.t("billingLocationsExport.isActive")]: data.isActive
          ? i18next.t("enums.answers.yes")
          : i18next.t("enums.answers.no"),
        [i18next.t("billingLocationsExport.name")]: data.name,
        [i18next.t("billingLocationsExport.householdMembersCount")]: Number(data.householdMembersCount),
        [i18next.t("billingLocationsExport.enableTime")]: enableTime ? enableTime.date : "",
        [i18next.t("billingLocationsExport.currentWaste")]: Number(data.currentWaste),
        [i18next.t("billingLocationsExport.netAmount")]: Number(data.netAmount),
        [i18next.t("billingLocationsExport.realAmount")]: Number(data.realAmount),
        [i18next.t("billingLocationsExport.balance")]: Number(data.balance),
        [i18next.t("billingLocationsExport.isPaid")]: data.isPaid
          ? i18next.t("enums.answers.yes")
          : i18next.t("enums.answers.no"),
      };
    }
    return {};
  });
};

export const formatWagonsReportDataForXlsx = (xlsxData: IReportItem[], enums: IServerEnumObject[]) => {
  return xlsxData.map((data) => {
    const date = getDateTimeStrings(new Date(data.date));

    const containerTypeResourceCode =
      enums
        .find((item) => item.name === EnumName.ContainerTypes)
        ?.values.find((item) => item.value === data.containerType)?.resourceCode ?? "";

    return {
      [i18next.t("wagonsReport.date")]: `${date.date} ${date.time}`,
      [i18next.t("wagonsReport.location")]: data.location,
      [i18next.t("wagonsReport.containerType")]: i18next.t(containerTypeResourceCode),
      [`${i18next.t("wagonsReport.weight")} (${i18next.t("kg")})`]: Number(data.weight),
      [i18next.t("wagonsReport.rfid")]: data.rfid,
      [i18next.t("wagonsReport.plateNumber")]: data.plateNumber,
    };
  });
};
